import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../common/Button';
import Modal from '../common/Modal';
import Input from '../common/Input';
import { ROUTES } from '../../utils/constants';

export default function Header() {
  const { isAuthenticated, login, logout } = useAuth();
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await login(password);

    if (result.success) {
      setShowLoginModal(false);
      setPassword('');
    } else {
      setError(result.error || 'Invalid password');
    }

    setIsLoading(false);
  };


  const handleLogout = () => {
    logout();
    setShowMenu(false);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setShowMenu(false);
  };

  const section1Items = [
    { label: 'Manage Players', path: ROUTES.PLAYERS, icon: '👥' },
    { label: 'Manage Courses', path: ROUTES.COURSES, icon: '⛳' },
  ];

  const section2Items = [
    { label: 'Manage Events', path: ROUTES.EVENTS, icon: '🏆' },
  ];

  const section3Items = [
    { label: 'View Results', path: '/events/history', icon: '📊' },
  ];

  return (
    <>
      <header className="bg-white shadow-medium relative z-50 border-b-4 border-primary">
        <div className="w-full px-4 py-4">
          <div className="flex items-center justify-center relative">
            {/* Logo - Centered */}
            <Link to={ROUTES.HISTORY} className="flex items-center hover:opacity-80 transition-opacity">
              <img
                src="/images/logo.png"
                alt="The Honourable Society of The Missing Links"
                className="h-14 w-auto md:h-16"
              />
            </Link>

            {/* Hamburger Menu Button - Absolute Right */}
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="absolute right-0 w-12 h-12 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
              aria-label="Menu"
            >
              <div className="w-6 h-5 flex flex-col justify-between">
                <span className={`block h-0.5 w-full bg-primary transition-all ${showMenu ? 'rotate-45 translate-y-2' : ''}`}></span>
                <span className={`block h-0.5 w-full bg-primary transition-all ${showMenu ? 'opacity-0' : ''}`}></span>
                <span className={`block h-0.5 w-full bg-primary transition-all ${showMenu ? '-rotate-45 -translate-y-2' : ''}`}></span>
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Sliding Drawer Menu */}
      <div
        className={`fixed inset-0 z-40 transition-opacity ${showMenu ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50"
          onClick={() => setShowMenu(false)}
        ></div>

        {/* Menu Panel */}
        <div
          className={`absolute top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl transform transition-transform ${
            showMenu ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Menu Header */}
          <div className="bg-gradient-to-r from-primary to-secondary text-white px-4 py-5 flex items-center justify-between">
            <h2 className="text-xl font-bold">Admin Menu</h2>
            <button
              onClick={() => setShowMenu(false)}
              className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
              aria-label="Close menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Menu Content */}
          <div className="flex flex-col h-[calc(100%-80px)]">
            {isAuthenticated ? (
              <>
                {/* Admin Navigation */}
                <div className="flex-1 overflow-y-auto">
                  <div className="p-4 space-y-6">

                    {/* SECTION 1: Player & Course Setup */}
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3 px-2">
                        Player & Course Setup
                      </p>
                      <nav className="space-y-1">
                        {section1Items.map((item) => (
                          <button
                            key={item.path}
                            onClick={() => handleNavigate(item.path)}
                            className="w-full flex items-center gap-4 px-4 py-4 text-left text-gray-700 hover:bg-sand hover:text-primary transition-colors rounded-lg min-h-touch-lg"
                          >
                            <span className="text-2xl">{item.icon}</span>
                            <span className="text-lg font-semibold">{item.label}</span>
                          </button>
                        ))}
                      </nav>
                    </div>

                    {/* SECTION 2: Event Setup */}
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3 px-2">
                        Event Setup
                      </p>
                      <p className="text-sm text-gray-600 mb-3 px-2">
                        Create events, assign players to groups, and manage live rounds.
                      </p>
                      <nav className="space-y-1">
                        {section2Items.map((item) => (
                          <button
                            key={item.path}
                            onClick={() => handleNavigate(item.path)}
                            className="w-full flex items-center gap-4 px-4 py-4 text-left text-gray-700 hover:bg-sand hover:text-primary transition-colors rounded-lg min-h-touch-lg"
                          >
                            <span className="text-2xl">{item.icon}</span>
                            <span className="text-lg font-semibold">{item.label}</span>
                          </button>
                        ))}
                      </nav>
                    </div>

                    {/* SECTION 3: Results History */}
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3 px-2">
                        Results History
                      </p>
                      <p className="text-sm text-gray-600 mb-3 px-2">
                        View completed TML events with full results and leaderboards.
                      </p>
                      <nav className="space-y-1">
                        {section3Items.map((item) => (
                          <button
                            key={item.path}
                            onClick={() => handleNavigate(item.path)}
                            className="w-full flex items-center gap-4 px-4 py-4 text-left text-gray-700 hover:bg-sand hover:text-primary transition-colors rounded-lg min-h-touch-lg"
                          >
                            <span className="text-2xl">{item.icon}</span>
                            <span className="text-lg font-semibold">{item.label}</span>
                          </button>
                        ))}
                      </nav>
                    </div>

                  </div>
                </div>

                {/* Logout Button */}
                <div className="p-4 border-t border-gray-200">
                  <Button
                    variant="secondary"
                    onClick={handleLogout}
                    className="w-full"
                  >
                    Logout
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Not Authenticated */}
                <div className="flex-1 flex items-center justify-center p-6">
                  <div className="text-center">
                    <div className="text-6xl mb-4">🔒</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Admin Access Required</h3>
                    <p className="text-gray-600 mb-6">
                      Please log in to access admin functions
                    </p>
                    <Button
                      variant="primary"
                      onClick={() => {
                        setShowMenu(false);
                        setShowLoginModal(true);
                      }}
                    >
                      Admin Login
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Login Modal */}
      <Modal
        isOpen={showLoginModal}
        onClose={() => {
          setShowLoginModal(false);
          setPassword('');
          setError('');
        }}
        title="Admin Login"
        size="sm"
      >
        <form onSubmit={handleLogin}>
          <Input
            label="Admin Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={error}
            placeholder="Enter admin password"
            required
            autoFocus
          />

          <div className="flex gap-3 mt-6">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowLoginModal(false);
                setPassword('');
                setError('');
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!password || isLoading}
              className="flex-1"
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
